import { useEffect } from 'react'
import { formatPrice } from '../../../shared/money'
import { SHOW_DATE, parseDate } from '../../../shared/show'
import type { Sale, Show, ShowItem } from '../../lib/ppc'

/**
 * The two things that get printed.
 *
 * A market stall runs on paper, and for a reason that has nothing to do with
 * nostalgia: the phone is the till, and the person holding it cannot also be
 * the person ticking plants off a list at the truck. Paper works with no
 * signal, no battery, and two pairs of hands.
 *
 * Both sheets render into the normal page and are hidden on screen. The print
 * stylesheet hides everything else, so what comes out of the printer is the
 * sheet and nothing around it.
 */

const longDate = (at: Date | null) =>
  at ? at.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''

/** Fire the print dialog once the sheet has actually rendered. */
function usePrintOnMount(onDone: () => void) {
  useEffect(() => {
    // A frame, so the browser has laid the sheet out before it is captured.
    const id = requestAnimationFrame(() => {
      window.print()
      onDone()
    })
    return () => cancelAnimationFrame(id)
  }, [onDone])
}

/**
 * The packing list.
 *
 * Ordered by TAG, not by price or name. At the truck the question is always
 * "have I got PL-014?", so the list has to be scannable in the order the tags
 * run. A tick box per row, because the point of carrying it is marking things off.
 *
 * Prices are on it deliberately: the same sheet doubles as the price list at
 * the table when the phone is busy taking a payment.
 */
export function PackingList({
  show, items, onDone,
}: {
  show: Show
  items: ShowItem[]
  onDone: () => void
}) {
  usePrintOnMount(onDone)

  const ordered = [...items].sort((a, b) => a.code.localeCompare(b.code))
  const units = ordered.reduce((n, i) => n + (i.is_batch ? i.quantity : 1), 0)
  const unpriced = ordered.filter((i) => i.asking_price_cents === null)

  return (
    <section className="print-sheet">
      <header>
        <h1>{show.name}</h1>
        <p>
          Packing list · Paradise Property Care · Vieques, Puerto Rico
          <br />
          {longDate(parseDate(show.starts_on ?? SHOW_DATE))}
        </p>
      </header>

      <p className="print-summary">
        <b>{ordered.length}</b> record{ordered.length === 1 ? '' : 's'} ·{' '}
        <b>{units}</b> unit{units === 1 ? '' : 's'} to load
      </p>

      {/* Said on the sheet, not only on the screen: an untagged plant at a
          table gets sold for whatever someone offers. */}
      {unpriced.length > 0 && (
        <p className="print-warn">
          {unpriced.length} of these has no asking price: {unpriced.map((i) => i.code).join(', ')}
        </p>
      )}

      <table>
        <thead>
          <tr>
            <th className="tick"> </th>
            <th>Tag</th>
            <th>Plant</th>
            <th>Qty</th>
            <th className="right">Price</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((item) => (
            <tr key={item.id}>
              <td className="tick"><span className="box" /></td>
              <td className="mono">{item.code}</td>
              <td>
                {item.nickname || item.common_name || '—'}
                {item.nickname && item.common_name ? <span className="dim"> · {item.common_name}</span> : null}
              </td>
              <td>{item.is_batch ? item.quantity : 1}</td>
              <td className="right">{formatPrice(item.asking_price_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer>
        <p>Loaded by ______________________ Checked by ______________________</p>
        <p className="dim">Tick on the way out, and again on the way home.</p>
      </footer>
    </section>
  )
}

/**
 * A receipt for one sale.
 *
 * Prints what was actually agreed, including how it was paid and whether the
 * money has been seen. An ATH sale that is still AWAITING says so on the
 * customer's copy — handing someone a receipt that says "paid" before the
 * transfer has landed is how a stall loses a plant and the money both.
 */
export function Receipt({
  show, sale, onDone,
}: {
  show: Show
  sale: Sale
  onDone: () => void
}) {
  usePrintOnMount(onDone)

  const paid = sale.status === 'paid' || sale.status === 'refunded'

  return (
    <section className="print-sheet receipt">
      <header>
        <h1>Paradise Property Care</h1>
        <p>Vieques, Puerto Rico · Dillon Forgues · 586.422.2996</p>
      </header>

      <p className="print-summary">
        {show.name}
        <br />
        {new Date(sale.created_at).toLocaleString()}
      </p>

      <table>
        <thead>
          <tr><th>Tag</th><th>Plant</th><th>Qty</th><th className="right">Each</th><th className="right">Total</th></tr>
        </thead>
        <tbody>
          {(sale.lines ?? []).map((line) => (
            <tr key={line.id}>
              <td className="mono">{line.code}</td>
              <td>{line.nickname ?? '—'}</td>
              <td>{line.quantity}</td>
              <td className="right">{formatPrice(line.unit_price_cents)}</td>
              <td className="right">{formatPrice(line.unit_price_cents * line.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="print-totals">
        <dt>Subtotal</dt><dd>{formatPrice(sale.subtotal_cents)}</dd>
        {sale.discount_cents > 0 && (<><dt>Discount</dt><dd>−{formatPrice(sale.discount_cents)}</dd></>)}
        <dt className="big">Total</dt><dd className="big">{formatPrice(sale.total_cents)}</dd>
        {sale.refund_cents > 0 && (<><dt>Refunded</dt><dd>−{formatPrice(sale.refund_cents)}</dd></>)}
      </dl>

      <p className="print-summary">
        Paid by {sale.method === 'ath' ? 'ATH Business' : 'cash'} ·{' '}
        {paid ? 'Received' : <b>AWAITING PAYMENT</b>}
      </p>

      {!paid && (
        <p className="print-warn">
          This is not a proof of payment. The transfer had not been confirmed when this was printed.
        </p>
      )}

      <footer>
        <p className="dim">Thank you. Plants are living things — no returns once they leave the table.</p>
      </footer>
    </section>
  )
}
